import { useCallback, useMemo } from 'react';
import { useMetroData } from '../hooks/useMetroData';
import { useUrlParamSeed } from '../lib/use-url-param-seed';
import { useCompareSelectionStore } from '../stores/compareSelectionStore';
import { SectionNav } from '../components/layout/SectionNav';
import { CewSiteFooter } from '../components/layout/CewSiteFooter';
import { LoadingState } from '../components/shared/LoadingState';
import { ErrorState } from '../components/shared/ErrorState';
// IntroModal + INTRO_COPY removed 2026-07-02 (Georgetown feedback).
import { MetroAreaCombobox } from '../components/shared/MetroAreaCombobox';
import { RatioMethodologyNote } from '../components/rankings/RatioMethodologyNote';
import { TableActions } from '../components/shared/TableActions';
import { toCsv, type CsvColumn } from '../lib/csv';
import { CompareBarChart } from '../components/compare/CompareBarChart';
// CompareRadarChart removed 2026-07-02 (V2 feedback) — no longer
// rendered on the Compare page; the component was deleted in the
// follow-up dead-code sweep.
import { TableSourceNote } from '../components/shared/TableSourceNote';
import type { Metro } from '../types/metro';
import { cn } from '../lib/utils';

/**
 * Side-by-side metro comparison. Three columns, each independently
 * pointed at a metro from the dataset.
 *
 * 2026-06-05 (Maria): dropped the rank-based cell tinting (green /
 * gold / rose for highest / middle / lowest). The color encoding
 * competed with the per-metric ratio pill and dilated the table
 * visually; raw numbers are easier to read against a neutral
 * background and the bar chart below already surfaces the
 * comparison visually.
 *
 * Layout: a CSS grid with 4 columns on desktop (label + 3 metros).
 * On mobile the grid collapses to a single column and each metro
 * stack repeats the metric labels inline so the structure still
 * reads top-to-bottom.
 *
 * Data wiring: same `useMetroData()` hook the Map + Rankings pages
 * use, so the Compare list is in lockstep with whatever the API
 * returns. No filter store binding — each column maintains its
 * own selection, since the cross-page filter is for narrowing the
 * universe of metros, not picking a comparison set.
 */

const NUM_COLUMNS = 3;

interface MetricRow {
  readonly key: string;
  readonly label: string;
  readonly description?: string;
  /** Numeric value extractor — used for rank coloring + display. */
  readonly numericValue?: (m: Metro) => number | null | undefined;
  /** Custom display for numeric metrics (defaults to compact number). */
  readonly format?: (v: number) => string;
}

const FORMAT_NUMBER = new Intl.NumberFormat('en-US');
const FORMAT_PERCENT = (v: number) => `${(v * 100).toFixed(1)}%`;
const FORMAT_RATIO = (v: number) => v.toFixed(2);

// 2026-06-05 (Maria): dropped three rows -- "Alignment Status",
// "Total Credentials", "Total Jobs". Status was redundant with the
// continuous ratio row below it (same signal, less granular); the two
// "Total" rows summed across all springboard fields and added bulk
// without sharpening cross-metro comparison. Remaining rows focus on
// ratio + springboard composition.
// 2026-07-02 (V2 feedback): row labels + descriptions rewritten so the
// Compare table reads consistently with Rankings + Detail.
//   Per-metro credentials-to-jobs ratio          -> Average credentials-to-jobs ratio.
//   Springboard Occupations                       -> Springboard occupations (sentence case)
//   Count of distinct middle-skill occupations... -> Number of distinct middle-skills springboard occupations identified locally.
//   Springboard Employment Share                  -> Springboard employment share
const ROWS: ReadonlyArray<MetricRow> = [
  {
    key: 'ratio',
    label: 'Credentials-to-jobs ratio',
    description: 'Average credentials-to-jobs ratio.',
    numericValue: (m) => m.ratio,
    format: FORMAT_RATIO,
  },
  {
    key: 'numSb',
    label: 'Springboard occupations',
    description:
      'Number of distinct middle-skills springboard occupations identified locally.',
    numericValue: (m) => m.numSb ?? null,
    format: (v) => FORMAT_NUMBER.format(v),
  },
  {
    key: 'shareSb',
    label: 'Springboard employment share',
    description: 'Share of middle-skills workers employed in springboard occupations.',
    numericValue: (m) => m.shareSb ?? null,
    format: FORMAT_PERCENT,
  },
];

// Rank-based tints (`tintsForRow` + TINT_CLASSES + STATUS_TINT_CLASS)
// were removed 2026-06-05 (Maria). The table now renders every cell
// with the same neutral chrome; the bar chart below carries the
// visual comparison.
const NEUTRAL_CELL_CLASS = 'bg-white text-text-1 border-border';

interface MetroSelectProps {
  readonly metros: ReadonlyArray<Metro>;
  readonly selectedId: string | null;
  readonly disabledIds: ReadonlyArray<string>;
  readonly onChange: (id: string | null) => void;
  readonly columnIndex: number;
}

/**
 * Per-column metro picker for the Compare page. Uses the shared
 * `MetroAreaCombobox` (APG combobox pattern) so all three columns
 * gain typeahead -- the user can jump to one of the 55 metros
 * without scrolling through a flat dropdown three times. Matches
 * Detail and Providers, where the Metro filter is also a combobox
 * (see PR #78 / DEC for IMP-003).
 *
 * Duplicate-pick prevention: `MetroAreaCombobox` does not currently
 * support a `disabledValues` prop (the old `BrandSelect` did). We
 * preserve the prior contract -- a metro picked in one column must
 * not be pickable in another -- by filtering it out of the `metros`
 * list this column receives. The currently selected metro for THIS
 * column stays in its own list (otherwise the combobox can't render
 * its display name from the id). Empty / unselected columns simply
 * see "every metro except the ones the other two columns hold".
 *
 * Label per column ("Metro 1", "Metro 2", "Metro 3") is preserved
 * so URL-seed assertions + screen-reader narration both still bind
 * to a stable accessible name.
 */
function MetroSelect({ metros, selectedId, disabledIds, onChange, columnIndex }: MetroSelectProps) {
  const visibleMetros = useMemo(
    () => metros.filter((m) => m.id === selectedId || !disabledIds.includes(m.id)),
    [metros, selectedId, disabledIds],
  );
  return (
    <MetroAreaCombobox
      label={`Metro ${columnIndex + 1}`}
      value={selectedId ?? ''}
      onChange={(v) => onChange(v || null)}
      metros={visibleMetros}
      placeholder="Search metros…"
    />
  );
}

export default function CompareView() {
  const { metros, isLoading, error, retry } = useMetroData();
  // IMP-006 -- column selections live in a zustand singleton so they
  // survive tab navigation (Maria 2026-06-01). The store mirrors the
  // previous local-state shape (3 nullable slots) one-for-one.
  const selectedIds = useCompareSelectionStore((s) => s.selectedIds);
  const setColumnInStore = useCompareSelectionStore((s) => s.setColumn);
  const setSelectedIdsInStore = useCompareSelectionStore((s) => s.setSelectedIds);
  const clearAllInStore = useCompareSelectionStore((s) => s.clearAll);

  // URL param seeding for the Map / Rankings cross-tab "Add to
  // comparison" links (Maria 2026-05-26 ask #3; 2026-05-29 drawer
  // nav hot-fix). Reads `?metroIds=a,b,c` and pre-populates the
  // first N slots once both the URL params AND the metros list are
  // ready. Defensive: silently ignores malformed input, blank
  // segments, and ids that don't match any metro -- a broken link
  // must not break the page.
  //
  // Uses the shared useUrlParamSeed hook (DEC-061) which guarantees
  // one-shot per mount; the previous inline useSearchParams +
  // useEffect + hasSeededRef machinery is consolidated here.
  useUrlParamSeed('metroIds', metros.length > 0, (raw) => {
    if (!raw) return;
    const requestedIds = raw
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    const resolvedIds: Array<string | null> = [];
    for (const id of requestedIds) {
      if (resolvedIds.length >= NUM_COLUMNS) break;
      const match = metros.find((m) => m.id === id);
      if (match && !resolvedIds.includes(match.id)) {
        resolvedIds.push(match.id);
      }
    }
    if (resolvedIds.length > 0) {
      // Read the current store value imperatively (avoids putting
      // `selectedIds` in the seed-effect's dep array, which would
      // defeat the useUrlParamSeed one-shot semantics).
      const prev = useCompareSelectionStore.getState().selectedIds;
      const next: Array<string | null> = [...prev];
      for (let i = 0; i < resolvedIds.length && i < NUM_COLUMNS; i += 1) {
        next[i] = resolvedIds[i] ?? null;
      }
      setSelectedIdsInStore(next);
    }
  });

  const selectedMetros = useMemo(
    () => selectedIds.map((id) => metros.find((m) => m.id === id) ?? null),
    [selectedIds, metros],
  );
  const otherSelected = (idx: number) =>
    selectedIds.filter((id, i) => i !== idx && id !== null) as ReadonlyArray<string>;

  // setColumn / clearAll wrap the store setters so the rest of the
  // component body reads the same as before the IMP-006 refactor.
  function setColumn(idx: number, id: string | null) {
    setColumnInStore(idx, id);
  }

  function clearAll() {
    clearAllInStore();
  }

  const anySelected = selectedMetros.some((m) => m !== null);

  // CSV serializer for the Copy / Export / Print toolbar in the page
  // header. Shape:
  //   - first column is the metric label ("Metric"),
  //   - one column per selected metro, labeled with the metro name
  //     ("Houston-The Woodlands, TX", ...).
  // Empty when fewer than 2 metros are selected so the existing
  // "Nothing to copy" warn-toast fires -- matches the chart panel's
  // own "pick 2 metros to begin" empty state. Wired 2026-06-09
  // (Slack Luis + Maria) -- CompareView never had a toolbar at all
  // before this change, so all three actions were inaccessible.
  const getCsvData = useCallback(() => {
    const picked = selectedMetros.filter((m): m is Metro => m !== null);
    if (picked.length < 2) return '';
    type CompareRow = { readonly label: string; readonly cells: ReadonlyArray<string> };
    const rows: ReadonlyArray<CompareRow> = ROWS.map((row) => {
      const cells = picked.map((m) => {
        const v = row.numericValue ? row.numericValue(m) : null;
        if (typeof v !== 'number') return '—';
        return row.format ? row.format(v) : FORMAT_NUMBER.format(v);
      });
      return { label: row.label, cells };
    });
    const columns: ReadonlyArray<CsvColumn<CompareRow>> = [
      { label: 'Metric', value: (r) => r.label },
      ...picked.map((m, i) => ({
        label: m.name,
        value: (r: CompareRow) => r.cells[i] ?? '',
      })),
    ];
    return toCsv(rows, columns);
  }, [selectedMetros]);

  return (
    <>
      <SectionNav />
      <div className="mx-auto max-w-page px-8 pt-8">
        <header className="mb-4 flex flex-col gap-2">
          <h1 className="text-[clamp(1.25rem,3vw,1.75rem)] font-bold text-navy">
            Compare metros side by side
          </h1>
          {/* 2026-07-02 (V2 feedback): tightened per the Georgetown
              spec — the previous copy over-promised ("occupational
              mix, provider counts, and annual credential volumes")
              relative to what the page actually shows. New copy
              matches the three visible rows: ratio + composition. */}
          <p className="text-sm leading-[1.6] text-text-3">
            Select two or three metro areas. Compare credentials-to-jobs ratios overall and by occupational group.
          </p>
          {/* IMP-009 (Maria 2026-06-01 Zoom 9:23): ratio methodology
              pill — already on Home/Map/Rankings/Detail/Providers —
              surfaced here too so the affordance lives in a
              predictable spot across every data page. Compare has no
              dedicated FilterBar component, so the trigger sits in
              the page header next to the intro copy.
              2026-06-09 (Slack Luis + Maria): the Copy / Export /
              Print toolbar lives in the same row -- Compare also has
              no FilterBar to hang it on, so the page header is the
              only sensible spot. TableActions's getCsvData empties
              out when fewer than 2 metros are picked, so the existing
              "Nothing to copy" toast carries the empty-state messaging. */}
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <RatioMethodologyNote />
            <TableActions
              getCsvData={getCsvData}
              filename="georgetown-cew-compare"
              className="ml-auto"
            />
          </div>
        </header>
      </div>

      <div className="bg-[#FCFCFD]">
        <section className="mx-auto flex max-w-page flex-col gap-6 px-8 pb-12 pt-6">
          {isLoading && <LoadingState label="Loading metros…" />}
          {error && (
            <ErrorState title="Cannot load metros" message={error.message} onRetry={retry} />
          )}

          {!isLoading && !error && (
            <div className="overflow-hidden rounded-lg border border-border bg-white">
              {/* Header row: label gutter + 3 metro selectors */}
              <div className="compare-grid grid grid-cols-1 gap-0 md:grid-cols-[220px_repeat(3,minmax(0,1fr))]">
                <div className="hidden border-b border-border bg-ice px-5 py-4 md:flex md:items-end md:justify-between">
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-text-4">
                    Metric
                  </span>
                  {anySelected && (
                    <button
                      type="button"
                      onClick={clearAll}
                      // A11y (WCAG 1.4.3): blue-link (6.48:1), not base blue
                      // (#0074CC = 4.30:1 on the ice filter-bar bg).
                      className="text-xs font-semibold text-blue-link underline-offset-2 hover:underline"
                    >
                      Clear all
                    </button>
                  )}
                </div>
                {selectedIds.map((id, idx) => (
                  <div
                    key={idx}
                    className={cn(
                      'border-b border-border bg-ice p-4',
                      idx > 0 && 'md:border-l md:border-border',
                    )}
                  >
                    <MetroSelect
                      metros={metros}
                      selectedId={id}
                      disabledIds={otherSelected(idx)}
                      onChange={(nextId) => setColumn(idx, nextId)}
                      columnIndex={idx}
                    />
                  </div>
                ))}
              </div>

              {/* Metric rows */}
              {ROWS.map((row, rowIdx) => {
                const numericValues = row.numericValue
                  ? selectedMetros.map((m) => (m ? row.numericValue!(m) : null))
                  : null;

                return (
                  <div
                    key={row.key}
                    className={cn(
                      'compare-grid grid grid-cols-1 gap-0 md:grid-cols-[220px_repeat(3,minmax(0,1fr))]',
                      rowIdx > 0 && 'border-t border-border/70',
                    )}
                  >
                    {/* Label gutter */}
                    <div className="border-b border-border bg-[#FAFBFC] px-5 py-4 md:border-b-0 md:border-r md:border-border">
                      <div className="text-sm font-semibold text-navy">{row.label}</div>
                      {row.description && (
                        <div className="mt-1 text-xs leading-[1.5] text-text-4">
                          {row.description}
                        </div>
                      )}
                    </div>

                    {/* 3 metro cells. 2026-06-05 (Maria): every cell
                        renders with the same neutral chrome --
                        rank-based green / gold / rose tints removed. */}
                    {selectedMetros.map((m, colIdx) => {
                      let cellLabel = '—';

                      if (m && row.numericValue) {
                        const v = numericValues![colIdx];
                        if (typeof v === 'number') {
                          cellLabel = row.format ? row.format(v) : FORMAT_NUMBER.format(v);
                        }
                      }

                      // Show the metric label inline on mobile (where
                      // the grid collapses to a single column and the
                      // gutter sits above each metro stack rather than
                      // to the left of it).
                      return (
                        <div
                          key={colIdx}
                          className={cn(
                            'flex flex-col gap-1 border-t border-border/70 px-4 py-4 md:border-t-0',
                            colIdx > 0 && 'md:border-l md:border-border',
                          )}
                        >
                          <span className="text-[10px] font-semibold uppercase tracking-wider text-text-4 md:hidden">
                            {m ? m.name : `Metro ${colIdx + 1}`}
                          </span>
                          <div
                            className={cn(
                              'inline-flex w-fit items-center rounded-md border px-3 py-1.5 text-base font-bold',
                              NEUTRAL_CELL_CLASS,
                            )}
                          >
                            {cellLabel}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          )}

          {/* Per-occupational-group bar comparison. Internally
              conditional on >=2 metros selected; the empty-state hint
              card surfaces from CompareBarChart when fewer are
              selected.
              2026-07-02 (V2 feedback): the economy-shape radar chart
              was retired here — reviewers said the "closed shape"
              read as decorative rather than a comparison tool and the
              bar chart already carries the per-OG story. The
              CompareRadarChart component was deleted in the follow-up
              dead-code sweep. */}
          {!isLoading && !error && (
            <>
              <CompareBarChart selectedMetros={selectedMetros} />
              {/* 2026-07-02 (V2 feedback): shared source + methodology
                  note below the comparison charts. */}
              <TableSourceNote />
            </>
          )}

          {/* Legend block removed 2026-06-05 (Maria) along with the
              rank-based cell tints it was explaining. */}
        </section>
      </div>
      <CewSiteFooter />

    </>
  );
}
